"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@/lib/context/user-context';
import { supabase } from '@/core/supabase';

interface UsageInfo {
  isPremium: boolean | null;
  isBasicPlanLite: boolean | null;
  planType: 'free' | 'basic' | 'basic_lite' | 'premium' | null;
  planName: string | null;
  remainingTranscripts: number;
  transcriptLimit: number;
  transcriptMinutes: { // 新增：分钟额度
    remaining: number;
    limit: number;
    used: number;
  } | null;
  remainingChats: number;
  chatLimit: number;
  chatAvailable: boolean;
}

interface UsageContextType {
  usageInfo: UsageInfo;
  loading: boolean;
  refreshUsage: () => Promise<void>;
  decrementTranscripts: () => void;
  decrementChats: () => void;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export function UsageProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useUser();
  const [usageInfo, setUsageInfo] = useState<UsageInfo>({
    isPremium: null,
    isBasicPlanLite: null,
    planType: null,
    planName: null,
    remainingTranscripts: 3,
    transcriptLimit: 3,
    transcriptMinutes: null,
    remainingChats: 0,
    chatLimit: 0,
    chatAvailable: false
  });
  const [loading, setLoading] = useState(true);
  const inFlightRef = React.useRef<Promise<void> | null>(null);
  const lastFetchedAtRef = React.useRef<number>(0);
  const TTL_MS = 30000; // 30s dedupe window to avoid duplicate calls (StrictMode/dev)

  const fetchUsageInfo = useCallback(async () => {
    // 如果认证状态还在加载中，保持 usage loading 为 true，等待认证完成
    if (authLoading) {
      return;
    }

    if (!user) {
      setUsageInfo({
        isPremium: null,
        isBasicPlanLite: null,
        planType: null,
        planName: null,
        remainingTranscripts: 3,
        transcriptLimit: 3,
        transcriptMinutes: null,
        remainingChats: 0,
        chatLimit: 0,
        chatAvailable: false
      });
      setLoading(false);
      return;
    }

    // Dedupe within TTL
    const now = Date.now();
    if (inFlightRef.current) {
      await inFlightRef.current;
      return;
    }
    if (now - lastFetchedAtRef.current < TTL_MS) {
      // Recently fetched; skip and reuse existing data
      setLoading(false);
      return;
    }

    const doFetch = async (retryCount = 0) => {
      const MAX_RETRIES = 2;
      
      try {
        // 获取当前 session
        let { data: { session } } = await supabase.auth.getSession();
        
        // 如果没有 session 或 token，尝试刷新
        if (!session?.access_token) {
          console.log('🔄 Usage Context - No session, attempting to refresh...');
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError || !refreshData.session) {
            console.log('🔄 Usage Context - Failed to refresh session:', refreshError?.message);
            setLoading(false);
            return;
          }
          session = refreshData.session;
          console.log('✅ Usage Context - Session refreshed successfully');
        }

        const response = await fetch('/api/user/usage', {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📊 Usage Context - API response:', result);
          
          const data = result.data || result;
          const remaining = data.transcript?.remaining ?? data.remaining ?? 0;
          const isPremium = data.isPremium;
          const isBasicPlanLite = data.isBasicPlanLite;
          const planType = data.planType;
          const planName = data.planName;
          const remainingChats = data.chat?.remaining ?? 0;
          const chatLimit = data.chat?.limit ?? 0;
          const chatAvailable = data.chat?.isAvailable ?? false;
          const transcriptMinutes = data.transcript?.minutes || null;

          setUsageInfo({
            isPremium,
            isBasicPlanLite,
            planType,
            planName,
            remainingTranscripts: remaining,
            transcriptLimit: isPremium ? -1 : 3,
            transcriptMinutes,
            remainingChats,
            chatLimit,
            chatAvailable
          });
          lastFetchedAtRef.current = Date.now();
        } else if (response.status === 401 && retryCount < MAX_RETRIES) {
          // Token 可能过期，尝试刷新并重试
          console.log(`🔄 Usage Context - Got 401, refreshing token (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (!refreshError && refreshData.session) {
            console.log('✅ Usage Context - Token refreshed, retrying request...');
            // 递归重试，增加计数
            await doFetch(retryCount + 1);
            return; // 提前返回，避免执行 finally 中的 lastFetchedAtRef 更新
          } else {
            console.error('❌ Usage Context - Failed to refresh token:', refreshError?.message);
          }
        } else {
          console.error('❌ Usage API failed:', response.status, await response.text());
        }
      } catch (error) {
        console.error('Error fetching usage info:', error);
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    };

    // start request
    inFlightRef.current = doFetch();
    await inFlightRef.current;
  }, [user, authLoading]);

  // 初始化和用户变化时获取usage信息
  useEffect(() => {
    fetchUsageInfo();
  }, [fetchUsageInfo]);

  // 监听认证状态变化，确保登录后立即刷新usage信息
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Usage Context - Auth state changed:', event, !!session);
        
        // 处理所有需要刷新 usage 的事件
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session) {
          // 重置 TTL 以确保能够重新获取数据
          lastFetchedAtRef.current = 0;
          
          // 登录/恢复session后延迟一点时间确保用户metadata已更新
          setTimeout(async () => {
            console.log('🔄 Usage Context - Refreshing after auth event:', event);
            setLoading(true);
            await fetchUsageInfo();
          }, 300);
        } else if (event === 'SIGNED_OUT') {
          // 登出时重置为默认状态
          lastFetchedAtRef.current = 0;
          setUsageInfo({
            isPremium: null,
            isBasicPlanLite: null,
            planType: null,
            planName: null,
            remainingTranscripts: 3,
            transcriptLimit: 3,
            transcriptMinutes: null,
            remainingChats: 0,
            chatLimit: 0,
            chatAvailable: false
          });
          setLoading(false);
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [fetchUsageInfo]);

  // 监听页面可见性变化，当应用从后台恢复时刷新 usage
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('🔄 Usage Context - App became visible, refreshing usage');
        // 重置 TTL 并刷新
        lastFetchedAtRef.current = 0;
        fetchUsageInfo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchUsageInfo]);

  // 刷新usage信息（强制刷新，忽略 TTL）
  const refreshUsage = useCallback(async () => {
    // 重置 TTL 确保能够重新获取数据
    lastFetchedAtRef.current = 0;
    setLoading(true);
    await fetchUsageInfo();
  }, [fetchUsageInfo]);

  // 本地减少transcript计数（立即UI反馈）
  const decrementTranscripts = useCallback(() => {
    setUsageInfo(prev => ({
      ...prev,
      remainingTranscripts: Math.max(0, prev.remainingTranscripts - 1)
    }));
  }, []);

  // 本地减少chat计数（立即UI反馈）
  const decrementChats = useCallback(() => {
    setUsageInfo(prev => ({
      ...prev,
      remainingChats: Math.max(0, prev.remainingChats - 1)
    }));
  }, []);

  const value: UsageContextType = {
    usageInfo,
    loading,
    refreshUsage,
    decrementTranscripts,
    decrementChats
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
}
